import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getOrder,
  getOrderRefund,
  getPaymentBreakup,
  getQuote,
  listOrders,
  placeOrder,
  refreshOrder,
  requestRefund,
  verifyPayment,
} from "./orders-api";
import { openRazorpayCheckout } from "./razorpay-checkout";
import type { OrderDetails } from "./types";

const ORDERS = ["orders"] as const;
/** The credit balance in the top bar (`useWallet`); a paid order adds to it. */
const WALLET = ["wallet-balance"] as const;

export const useQuote = (enabled: boolean) =>
  useQuery({ queryKey: ["orders", "quote"], queryFn: getQuote, enabled, staleTime: 15_000 });

export const useOrder = (orderId: string) =>
  useQuery({ queryKey: ["orders", orderId], queryFn: () => getOrder(orderId) });

export const usePaymentBreakup = (orderId: string) =>
  useQuery({ queryKey: ["orders", orderId, "payment-breakup"], queryFn: () => getPaymentBreakup(orderId) });

export const useOrders = () =>
  useInfiniteQuery({
    queryKey: [...ORDERS, "list"],
    queryFn: ({ pageParam }) => listOrders(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before_id,
  });

/**
 * Pay for the checked-out cart: place the order, open Razorpay Checkout, then verify
 * the signed result with the server. Resolves with the verified order; rejects if the
 * buyer closes Checkout or a step fails.
 */
export function usePayForCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<OrderDetails> => {
      const placed = await placeOrder();
      const result = await openRazorpayCheckout(placed);
      return verifyPayment(placed.order.id, result);
    },
    onSettled: () => {
      // A paid cart is closed and its credits added; whatever happened, re-read them.
      void qc.invalidateQueries({ queryKey: ["cart"] });
      void qc.invalidateQueries({ queryKey: ORDERS });
      void qc.invalidateQueries({ queryKey: WALLET });
    },
  });
}

export function useRefreshOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refreshOrder(orderId),
    onSuccess: (order) => {
      qc.setQueryData(["orders", orderId], order);
      void qc.invalidateQueries({ queryKey: ["orders", orderId, "payment-breakup"] });
      void qc.invalidateQueries({ queryKey: ["cart"] });
      void qc.invalidateQueries({ queryKey: WALLET });
    },
  });
}

export const useOrderRefund = (orderId: string, enabled: boolean) =>
  useQuery({ queryKey: ["orders", orderId, "refund"], queryFn: () => getOrderRefund(orderId), enabled });

export function useRequestRefund(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => requestRefund(orderId, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["orders", orderId] });
    },
  });
}
